'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

interface PageHeroProps {
  title: string;
  breadcrumbs?: { label: string; href: string }[];
  background?: string;
  titleColor?: string;
  backgroundImage?: string;
  titleSize?: 'small' | 'medium' | 'large';
  description?: React.ReactNode;
}

const PageHero: React.FC<PageHeroProps> = ({
  title,
  breadcrumbs,
  background = 'bg-white',
  titleColor = 'text-primary',
  backgroundImage = '/images/hero-gradient-patients.jpg',
  titleSize = 'large',
  description,
}) => {
  return (
    <div className={`relative overflow-hidden ${background} pt-32 pb-8 sm:pb-12`}>
      <Image
        src={backgroundImage}
        alt=""
        aria-hidden="true"
        fill
        priority
        sizes="100vw"
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {breadcrumbs && (
          <nav aria-label="Breadcrumb" className="flex gap-2 mb-8 rh-rise">
            {breadcrumbs.map((item, index) => (
              <React.Fragment key={item.href}>
                {index > 0 && (
                  <span aria-hidden="true" className="text-default-600">
                    /
                  </span>
                )}
                <Link
                  href={item.href}
                  className="text-default-700 underline-offset-4 transition-colors duration-200 hover:text-primary hover:underline"
                >
                  {item.label}
                </Link>
              </React.Fragment>
            ))}
          </nav>
        )}
        <h1
          className={`${titleColor} rh-rise rh-rise-2 ${
            titleSize === 'small'
              ? 'text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl'
              : titleSize === 'medium'
                ? 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl'
                : 'text-4xl sm:text-5xl lg:text-6xl xl:text-7xl'
          }`}
        >
          {title}
        </h1>
        {description && (
          <div className="mt-4 max-w-3xl text-default-700 rh-rise rh-rise-3">{description}</div>
        )}
      </div>
    </div>
  );
};

export default PageHero;
